import { useState, useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { segmentService, profileService, userAdminService } from '@/services';
import { projectSegmentService, type ProjectSegmentWithRelations } from '@/services';
import { toast } from 'sonner';

interface RowState {
  checked: boolean;
  segment_manager: string | null;
  notes: string;
  existingRecord: ProjectSegmentWithRelations | null;
}

interface ManageSegmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  existingSegments: ProjectSegmentWithRelations[];
}

export default function ManageSegmentsDialog({
  open, onOpenChange, projectId, projectName, existingSegments,
}: ManageSegmentsDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [rows, setRows] = useState<Record<string, RowState>>({});

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const { data: allSegments = [], isLoading: segmentsLoading } = useQuery({
    queryKey: ['list-segments-active', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await segmentService.listActive(profile.company_id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: open && !!profile?.company_id,
  });

  const { data: managers = [] } = useQuery({
    queryKey: ['company-profiles', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await userAdminService.listUsers(profile.company_id);
      if (result.error) throw result.error;
      return [...result.data].sort((a, b) =>
        (a.full_name ?? '').localeCompare(b.full_name ?? ''),
      );
    },
    enabled: open && !!profile?.company_id,
  });

  // Build the existing segments lookup
  const existingMap = useMemo(() => {
    const map: Record<string, ProjectSegmentWithRelations> = {};
    for (const seg of existingSegments) {
      map[seg.segment_id] = seg;
    }
    return map;
  }, [existingSegments]);

  // Initialize rows when dialog opens or data changes
  useEffect(() => {
    if (!open || allSegments.length === 0) return;
    const newRows: Record<string, RowState> = {};
    for (const seg of allSegments) {
      const existing = existingMap[seg.id] || null;
      newRows[seg.id] = {
        checked: !!existing,
        segment_manager: existing?.segment_manager || null,
        notes: existing?.notes || '',
        existingRecord: existing,
      };
    }
    setRows(newRows);
  }, [open, allSegments, existingMap]);

  const updateRow = (segmentId: string, patch: Partial<RowState>) => {
    setRows(prev => ({ ...prev, [segmentId]: { ...prev[segmentId], ...patch } }));
  };

  const handleSave = async () => {
    if (!profile?.company_id || !user?.id) return;
    setIsSaving(true);
    const context = { companyId: profile.company_id, userId: user.id };
    let created = 0, updated = 0, deleted = 0, errors = 0;

    try {
      for (const [segmentId, row] of Object.entries(rows)) {
        const existing = row.existingRecord;

        if (row.checked && !existing) {
          // CREATE
          const result = await projectSegmentService.create(
            { project_id: projectId, segment_id: segmentId, segment_manager: row.segment_manager, notes: row.notes || null },
            context,
          );
          if (result.error) errors++; else created++;
        } else if (!row.checked && existing) {
          // DELETE
          const snapshot = { segment_id: existing.segment_id, segment_manager: existing.segment_manager, notes: existing.notes };
          const result = await projectSegmentService.delete(existing.id, snapshot, context);
          if (!result.success) errors++; else deleted++;
        } else if (row.checked && existing) {
          // UPDATE only if changed
          const managerChanged = (row.segment_manager || null) !== (existing.segment_manager || null);
          const notesChanged = (row.notes || '') !== (existing.notes || '');
          if (managerChanged || notesChanged) {
            const snapshot = { segment_id: existing.segment_id, segment_manager: existing.segment_manager, notes: existing.notes };
            const result = await projectSegmentService.update(
              existing.id,
              { segment_manager: row.segment_manager || null, notes: row.notes || null },
              snapshot,
              context,
            );
            if (result.error) errors++; else updated++;
          }
        }
      }

      const parts: string[] = [];
      if (created) parts.push(`${created} added`);
      if (updated) parts.push(`${updated} updated`);
      if (deleted) parts.push(`${deleted} removed`);
      if (errors) parts.push(`${errors} failed`);

      if (parts.length === 0) {
        toast.info('No changes to save');
      } else {
        toast.success(`Segments: ${parts.join(', ')}`);
      }

      queryClient.invalidateQueries({ queryKey: ['project-segments', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      onOpenChange(false);
    } catch (err) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = segmentsLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{projectName}</DialogTitle>
          <DialogDescription>Add segments to project</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <ScrollArea className="flex-1 -mx-6 px-6 max-h-[60vh] overflow-y-auto">
            <div className="divide-y divide-border">
              {allSegments.map((seg) => {
                const row = rows[seg.id];
                if (!row) return null;
                return (
                  <div key={seg.id} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={row.checked}
                        onCheckedChange={(checked) => updateRow(seg.id, { checked: !!checked })}
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-3">
                        <Label className="text-sm font-semibold text-foreground">
                          {seg.display_value}
                        </Label>

                        <div className="flex gap-4">
                          <div className="flex-1">
                            <Label className="text-xs text-muted-foreground mb-1 block">Segment Manager</Label>
                            <Select
                              value={row.segment_manager || '__none__'}
                              onValueChange={(v) => updateRow(seg.id, { segment_manager: v === '__none__' ? null : v })}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select a manager" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {managers.filter(m => m.id).map((m) => (
                                  <SelectItem key={m.id} value={m.id}>
                                    {m.full_name || 'Unnamed'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex-1">
                            <Label className="text-xs text-muted-foreground mb-1 block">Notes</Label>
                            <Textarea
                              rows={1}
                              value={row.notes}
                              onChange={(e) => updateRow(seg.id, { notes: e.target.value })}
                              className="text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
